import React, { useState, useEffect } from 'react'
import {toCdnUrl} from '../../../utils/cdnUrl'
import {
  getCategorizationStats,
  getCampaigns,
  getCampaignPosts,
  analyzeCampaign,
  finalizeCampaign,
  resetCampaign,
  getSuggestedLibraries,
  createLibraryFromSuggestion,
  getIndustries,
  getAccounts,
  getPublicLibraries
} from './core/_requests'
import moment from 'moment'
import 'moment/locale/he'
moment.locale('he')

interface Stats {
  totalActiveCampaigns: number
  categorizedCount: number
  pendingCount: number
  aiSuggestedCount: number
  finalizedCount: number
  withLibraryCount: number
  categorizationPercent: number
  libraryAssignmentPercent: number
}

interface Post {
  _id: string
  name?: string
  post_content?: string
  media_url?: string
  created_at?: string
  reply_text?: string
}

interface Campaign {
  _id: string
  name: string
  account?: { first_name?: string; last_name?: string; email?: string; company_name?: string; trial_period_end?: string }
  sample_posts?: Post[]
  post_count?: number
  categorization?: {
    _id: string
    status: 'pending' | 'ai_suggested' | 'finalized'
    industry?: { _id: string; name: string; name_he: string }
    location?: string
    suggested_library_name?: string
    assigned_library?: { _id: string; name: string }
    notes?: string
    ai_analysis?: {
      reasoning?: string
      confidence?: number
      detected_keywords?: string[]
      matched_existing_library?: { _id: string; name: string }
      match_reason?: string
      suggested_industry?: {
        should_create_new?: boolean
        new_category_name?: string
        new_category_name_he?: string
      }
    }
  }
}

interface SuggestedLibrary {
  _id: string
  name: string
  industry?: { _id: string; name: string; name_he: string }
  location?: string
  campaigns_count: number
  campaigns?: { _id: string; name: string }[]
  status: 'suggested' | 'creating' | 'created'
}

interface Industry {
  _id: string
  name: string
  name_he: string
  children?: Industry[]
}

interface Library {
  _id: string
  name: string
}

// Light theme colors
const colors = {
  bgPrimary: '#f5f8fa',
  bgSecondary: '#ffffff',
  bgTertiary: '#e8ecef',
  bgHover: '#e1e5ea',
  textPrimary: '#1e1e2d',
  textSecondary: '#5e6278',
  textMuted: '#a1a5b7',
  accentPrimary: '#3699ff',
  accentSuccess: '#1bc5bd',
  accentWarning: '#ffa800',
  accentDanger: '#f64e60',
  borderColor: 'rgba(0, 0, 0, 0.08)',
}

const CampaignCategorizationPage: React.FC = () => {
  // Stats
  const [stats, setStats] = useState<Stats | null>(null)

  // Campaigns
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [localSearch, setLocalSearch] = useState('')

  // Suggested libraries
  const [suggestedLibraries, setSuggestedLibraries] = useState<SuggestedLibrary[]>([])

  // Reference data
  const [industries, setIndustries] = useState<Industry[]>([])
  const [publicLibraries, setPublicLibraries] = useState<Library[]>([])
  const [accounts, setAccounts] = useState<any[]>([])
  const [selectedAccount, setSelectedAccount] = useState('')

  // UI state
  const [activeTab, setActiveTab] = useState<'campaigns' | 'suggested-libraries'>('campaigns')
  const [creatingLibrary, setCreatingLibrary] = useState<string | null>(null)

  // Posts viewer modal
  const [showPostsModal, setShowPostsModal] = useState(false)
  const [viewingCampaign, setViewingCampaign] = useState<Campaign | null>(null)
  const [campaignPosts, setCampaignPosts] = useState<Post[]>([])
  const [loadingPosts, setLoadingPosts] = useState(false)
  const [postsPage, setPostsPage] = useState(1)
  const [postsTotalPages, setPostsTotalPages] = useState(1)

  // Finalize modal
  const [showFinalizeModal, setShowFinalizeModal] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null)
  const [finalizeData, setFinalizeData] = useState({
    industry_id: '',
    location: '',
    library_name: '',
    assign_to_existing_library_id: '',
    create_new_industry: false,
    new_industry_name: '',
    new_industry_name_he: '',
    skip_library: false,
    notes: ''
  })
  const [finalizing, setFinalizing] = useState(false)
  const [analyzingInModal, setAnalyzingInModal] = useState(false)

  useEffect(() => {
    loadInitialData()
  }, [])

  useEffect(() => {
    if (activeTab === 'campaigns') {
      loadCampaigns()
    } else {
      loadSuggestedLibraries()
    }
  }, [activeTab, statusFilter, searchQuery, selectedAccount, currentPage])

  const loadInitialData = async () => {
    try {
      const [statsRes, industriesRes, librariesRes, accountsRes] = await Promise.all([
        getCategorizationStats(),
        getIndustries(true),
        getPublicLibraries(),
        getAccounts()
      ])
      setStats(statsRes)
      setIndustries(industriesRes.industries || [])
      setPublicLibraries(librariesRes.libraries || [])
      setAccounts(accountsRes.accounts || [])
    } catch (error) {
      console.error('Failed to load initial data:', error)
    }
  }

  const loadCampaigns = async () => {
    setLoading(true)
    try {
      const params: any = { page: currentPage, limit: 20 }
      if (statusFilter) params.status = statusFilter
      if (searchQuery) params.search = searchQuery
      if (selectedAccount) params.account_id = selectedAccount

      const res = await getCampaigns(params)
      setCampaigns(res.campaigns || [])
      setTotalPages(res.pagination?.pages || 1)
    } catch (error) {
      console.error('Failed to load campaigns:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadSuggestedLibraries = async () => {
    setLoading(true)
    try {
      const res = await getSuggestedLibraries()
      setSuggestedLibraries(res.suggestedLibraries || [])
    } catch (error) {
      console.error('Failed to load suggested libraries:', error)
    } finally {
      setLoading(false)
    }
  }

  const refreshStats = async () => {
    try {
      const statsRes = await getCategorizationStats()
      setStats(statsRes)
    } catch (error) {
      console.error('Failed to refresh stats:', error)
    }
  }

  const openPostsModal = async (campaign: Campaign, page = 1) => {
    setViewingCampaign(campaign)
    setShowPostsModal(true)
    setLoadingPosts(true)
    setPostsPage(page)
    try {
      const res = await getCampaignPosts(campaign._id, page, 20)
      setCampaignPosts(res.posts || [])
      setPostsTotalPages(res.pagination?.pages || 1)
    } catch (error) {
      console.error('Failed to load posts:', error)
    } finally {
      setLoadingPosts(false)
    }
  }

  const openFinalizeModal = async (campaign: Campaign) => {
    setSelectedCampaign(campaign)
    setShowFinalizeModal(true)

    // Refresh industries list to get any newly created ones
    try {
      const industriesRes = await getIndustries(true)
      setIndustries(industriesRes.industries || [])
    } catch (error) {
      console.error('Failed to refresh industries:', error)
    }

    // Pre-fill form with existing categorization data
    const cat = campaign.categorization
    const aiSuggestedNewIndustry = cat?.ai_analysis?.suggested_industry

    setFinalizeData({
      industry_id: cat?.industry?._id || '',
      location: cat?.location || '',
      library_name: cat?.suggested_library_name || '',
      assign_to_existing_library_id: cat?.ai_analysis?.matched_existing_library?._id || cat?.assigned_library?._id || '',
      // If AI suggested creating a new industry and no industry is set, pre-fill the new industry fields
      create_new_industry: !cat?.industry && aiSuggestedNewIndustry?.should_create_new || false,
      new_industry_name: aiSuggestedNewIndustry?.new_category_name || '',
      new_industry_name_he: aiSuggestedNewIndustry?.new_category_name_he || '',
      skip_library: false,
      notes: cat?.notes || ''
    })

    // Load posts for this campaign
    setLoadingPosts(true)
    setCampaignPosts([])
    try {
      const res = await getCampaignPosts(campaign._id, 1, 50)
      setCampaignPosts(res.posts || [])
    } catch (error) {
      console.error('Failed to load posts:', error)
    } finally {
      setLoadingPosts(false)
    }

  }

  const handleFinalize = async () => {
    if (!selectedCampaign) return
    setFinalizing(true)
    try {
      await finalizeCampaign(selectedCampaign._id, finalizeData)
      setShowFinalizeModal(false)
      await loadCampaigns()
      await refreshStats()
    } catch (error: any) {
      alert(error.message || 'Failed to finalize')
    } finally {
      setFinalizing(false)
    }
  }

  const handleResetCampaign = async (campaignId: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך לאפס את הסיווג?')) return
    try {
      await resetCampaign(campaignId)
      await loadCampaigns()
      await refreshStats()
    } catch (error: any) {
      alert(error.message || 'Failed to reset campaign')
    }
  }

  const handleAnalyzeInModal = async () => {
    if (!selectedCampaign) return
    setAnalyzingInModal(true)
    try {
      const result = await analyzeCampaign(selectedCampaign._id)

      // Refresh industries and libraries
      const [industriesRes, librariesRes] = await Promise.all([
        getIndustries(true),
        getPublicLibraries()
      ])
      setIndustries(industriesRes.industries || [])
      setPublicLibraries(librariesRes.libraries || [])

      // Fill form with AI results
      const analysis = result.analysis
      const cat = result.categorization

      setFinalizeData(prev => ({
        ...prev,
        industry_id: cat?.industry?._id || analysis?.industry?.existing_category_id || '',
        location: analysis?.location || cat?.location || prev.location,
        library_name: analysis?.suggested_library_name || cat?.suggested_library_name || prev.library_name,
        assign_to_existing_library_id: analysis?.matched_library?.id || cat?.ai_analysis?.matched_existing_library?._id || '',
        create_new_industry: analysis?.industry?.should_create_new || false,
        new_industry_name: analysis?.industry?.new_category_name || '',
        new_industry_name_he: analysis?.industry?.new_category_name_he || ''
      }))

      // Update selected campaign with new categorization data
      setSelectedCampaign(prev => prev ? {
        ...prev,
        categorization: cat
      } : null)

    } catch (error: any) {
      alert(error.message || 'Failed to analyze campaign')
    } finally {
      setAnalyzingInModal(false)
    }
  }

  const handleCreateLibrary = async (suggestedId: string) => {
    setCreatingLibrary(suggestedId)
    try {
      await createLibraryFromSuggestion(suggestedId)
      await loadSuggestedLibraries()
      await refreshStats()
    } catch (error: any) {
      alert(error.message || 'Failed to create library')
    } finally {
      setCreatingLibrary(null)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchQuery(localSearch)
    setCurrentPage(1)
  }

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'finalized':
        return { bg: 'rgba(17, 153, 142, 0.15)', color: '#11998e', text: 'הושלם' }
      case 'ai_suggested':
        return { bg: 'rgba(102, 126, 234, 0.15)', color: '#667eea', text: 'הצעת AI' }
      case 'pending':
        return { bg: 'rgba(255, 193, 7, 0.15)', color: '#ffc107', text: 'ממתין' }
      default:
        return { bg: 'rgba(94, 98, 120, 0.15)', color: colors.textSecondary, text: 'לא מסווג' }
    }
  }

  return (
    <div style={{ padding: 0, direction: 'rtl' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: 700,
          color: colors.textPrimary,
          margin: 0,
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          display: 'inline-block'
        }}>
          סיווג קמפיינים
        </h1>
        <p style={{ color: colors.textSecondary, marginTop: '8px', fontSize: '14px' }}>
          ניתוח וסיווג קמפיינים לפי תעשייה ומיקום, והקצאת ספריות קבוצות
        </p>
      </div>

      {/* Progress Section */}
      <div style={{
        background: colors.bgSecondary,
        borderRadius: '20px',
        padding: '24px',
        marginBottom: '32px',
        border: `1px solid ${colors.borderColor}`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ color: colors.textPrimary, margin: 0, fontSize: '16px' }}>התקדמות סיווג</h3>
          <span style={{ color: colors.textMuted, fontSize: '14px' }}>
            {stats?.finalizedCount || 0} מתוך {stats?.totalActiveCampaigns || 0} קמפיינים
          </span>
        </div>

        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '12px',
          background: colors.bgTertiary,
          borderRadius: '6px',
          overflow: 'hidden',
          marginBottom: '20px'
        }}>
          <div style={{
            width: `${stats?.categorizationPercent || 0}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #f093fb 0%, #f5576c 100%)',
            borderRadius: '6px',
            transition: 'width 0.5s ease'
          }} />
        </div>

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          {[
            { label: 'סה"כ קמפיינים', value: stats?.totalActiveCampaigns || 0, color: '#667eea' },
            { label: 'הצעות AI', value: stats?.aiSuggestedCount || 0, color: '#f093fb' },
            { label: 'הושלמו', value: stats?.finalizedCount || 0, color: '#11998e' },
            { label: 'עם ספרייה', value: stats?.withLibraryCount || 0, color: '#4facfe' },
          ].map((stat, i) => (
            <div key={i} style={{
              background: colors.bgTertiary,
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        background: colors.bgTertiary,
        padding: '8px',
        borderRadius: '12px',
        width: 'fit-content'
      }}>
        {[
          { id: 'campaigns', label: 'קמפיינים', icon: 'bi-megaphone' },
          { id: 'suggested-libraries', label: 'ספריות מוצעות', icon: 'bi-lightbulb' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id as any)
              setCurrentPage(1)
            }}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === tab.id
                ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                : 'transparent',
              color: activeTab === tab.id ? '#fff' : colors.textPrimary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: activeTab === tab.id ? 600 : 400,
              transition: 'all 0.2s ease'
            }}
          >
            <i className={`bi ${tab.icon}`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <>
          {/* Filters */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '24px',
            alignItems: 'center',
            background: colors.bgSecondary,
            padding: '16px 20px',
            borderRadius: '16px',
            border: `1px solid ${colors.borderColor}`,
            flexWrap: 'wrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}>
            <form onSubmit={handleSearch} style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
              <i className="bi bi-search" style={{
                position: 'absolute',
                right: '18px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.textSecondary,
              }}></i>
              <input
                type="text"
                placeholder="חיפוש קמפיינים..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 48px 12px 16px',
                  background: colors.bgTertiary,
                  border: `2px solid ${colors.borderColor}`,
                  borderRadius: '10px',
                  color: colors.textPrimary,
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </form>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1) }}
              style={{
                padding: '12px 16px',
                borderRadius: '10px',
                border: `2px solid ${colors.borderColor}`,
                background: colors.bgTertiary,
                color: colors.textPrimary,
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer',
                minWidth: '150px'
              }}
            >
              <option value="">כל הסטטוסים</option>
              <option value="uncategorized">לא מסווג</option>
              <option value="pending">ממתין</option>
              <option value="ai_suggested">הצעת AI</option>
              <option value="finalized">הושלם</option>
            </select>

            <select
              value={selectedAccount}
              onChange={(e) => { setSelectedAccount(e.target.value); setCurrentPage(1) }}
              style={{
                padding: '12px 16px',
                borderRadius: '10px',
                border: `2px solid ${colors.borderColor}`,
                background: colors.bgTertiary,
                color: colors.textPrimary,
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer',
                minWidth: '180px'
              }}
            >
              <option value="">כל החשבונות</option>
              {accounts.map(acc => (
                <option key={acc._id} value={acc._id}>
                  {acc.first_name} {acc.last_name}
                </option>
              ))}
            </select>

          </div>

          {/* Campaigns List */}
          <div style={{
            background: colors.bgSecondary,
            borderRadius: '20px',
            border: `1px solid ${colors.borderColor}`,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}>
            {loading ? (
              <div style={{ padding: '80px', textAlign: 'center' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: `3px solid ${colors.borderColor}`,
                  borderTopColor: '#f093fb',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  margin: '0 auto 20px'
                }}></div>
                <div style={{ color: colors.textSecondary }}>טוען קמפיינים...</div>
              </div>
            ) : campaigns.length === 0 ? (
              <div style={{ padding: '80px', textAlign: 'center' }}>
                <i className="bi bi-megaphone" style={{ fontSize: '48px', color: colors.textSecondary, display: 'block', marginBottom: '16px' }}></i>
                <div style={{ color: colors.textPrimary, fontSize: '16px', fontWeight: 600 }}>לא נמצאו קמפיינים</div>
              </div>
            ) : (
              campaigns.map((campaign) => {
                const status = getStatusBadge(campaign.categorization?.status)
                return (
                  <div
                    key={campaign._id}
                    style={{
                      padding: '20px 24px',
                      borderBottom: `1px solid ${colors.borderColor}`,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                          <span style={{ fontWeight: 600, color: colors.textPrimary, fontSize: '15px' }}>{campaign.name}</span>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 500,
                            background: status.bg,
                            color: status.color,
                          }}>
                            {status.text}
                          </span>
                        </div>
                        {campaign.account && (
                          <div style={{ fontSize: '13px', color: colors.textSecondary }}>
                            <i className="bi bi-person me-1"></i>
                            {campaign.account.first_name} {campaign.account.last_name}
                            {campaign.account.company_name && ` - ${campaign.account.company_name}`}
                          </div>
                        )}
                        {campaign.post_count && (
                          <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>
                            <i className="bi bi-file-text me-1"></i>
                            {campaign.post_count} פוסטים
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => openPostsModal(campaign)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: `1px solid ${colors.borderColor}`,
                            background: 'transparent',
                            color: colors.textPrimary,
                            fontSize: '13px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <i className="bi bi-eye"></i>
                          צפה בפוסטים
                        </button>

                        {campaign.categorization?.status === 'finalized' ? (
                          <>
                            <button
                              onClick={() => openFinalizeModal(campaign)}
                              style={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                border: `1px solid ${colors.borderColor}`,
                                background: 'transparent',
                                color: colors.textPrimary,
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                            >
                              <i className="bi bi-pencil"></i>
                              ערוך
                            </button>
                            <button
                              onClick={() => handleResetCampaign(campaign._id)}
                              style={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                border: 'none',
                                background: 'rgba(255, 99, 71, 0.15)',
                                color: '#ff6347',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                            >
                              <i className="bi bi-arrow-counterclockwise"></i>
                              אפס
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => openFinalizeModal(campaign)}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '8px',
                              border: 'none',
                              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                              color: '#fff',
                              fontSize: '13px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <i className="bi bi-check-lg"></i>
                            סיים סיווג
                          </button>
                        )}
                      </div>
                    </div>

                    {/* AI Analysis Results */}
                    {campaign.categorization?.ai_analysis && (
                      <div style={{
                        background: 'rgba(102, 126, 234, 0.08)',
                        borderRadius: '12px',
                        padding: '16px',
                        marginTop: '12px'
                      }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '12px' }}>
                          {campaign.categorization.industry && (
                            <div>
                              <span style={{ fontSize: '11px', color: colors.textMuted, display: 'block' }}>תעשייה</span>
                              <span style={{ fontSize: '14px', color: colors.textPrimary, fontWeight: 500 }}>
                                {campaign.categorization.industry.name_he}
                              </span>
                            </div>
                          )}
                          {campaign.categorization.location && (
                            <div>
                              <span style={{ fontSize: '11px', color: colors.textMuted, display: 'block' }}>מיקום</span>
                              <span style={{ fontSize: '14px', color: colors.textPrimary, fontWeight: 500 }}>
                                {campaign.categorization.location}
                              </span>
                            </div>
                          )}
                          {campaign.categorization.suggested_library_name && (
                            <div>
                              <span style={{ fontSize: '11px', color: colors.textMuted, display: 'block' }}>ספרייה מוצעת</span>
                              <span style={{ fontSize: '14px', color: '#f093fb', fontWeight: 500 }}>
                                {campaign.categorization.suggested_library_name}
                              </span>
                            </div>
                          )}
                          {campaign.categorization.ai_analysis.confidence && (
                            <div>
                              <span style={{ fontSize: '11px', color: colors.textMuted, display: 'block' }}>ביטחון</span>
                              <span style={{ fontSize: '14px', color: colors.textPrimary, fontWeight: 500 }}>
                                {campaign.categorization.ai_analysis.confidence}%
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Matched existing library */}
                        {campaign.categorization.ai_analysis.matched_existing_library && (
                          <div style={{
                            background: 'rgba(17, 153, 142, 0.15)',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '12px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#11998e', fontSize: '13px' }}>
                              <i className="bi bi-check-circle-fill"></i>
                              <span>נמצאה ספרייה קיימת מתאימה:</span>
                              <strong>{campaign.categorization.ai_analysis.matched_existing_library.name}</strong>
                            </div>
                            {campaign.categorization.ai_analysis.match_reason && (
                              <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>
                                {campaign.categorization.ai_analysis.match_reason}
                              </div>
                            )}
                          </div>
                        )}

                        {campaign.categorization.ai_analysis.reasoning && (
                          <div style={{ fontSize: '13px', color: colors.textMuted }}>
                            <i className="bi bi-info-circle me-1"></i>
                            {campaign.categorization.ai_analysis.reasoning}
                          </div>
                        )}

                        {campaign.categorization.ai_analysis.detected_keywords && campaign.categorization.ai_analysis.detected_keywords.length > 0 && (
                          <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {campaign.categorization.ai_analysis.detected_keywords.map((kw, i) => (
                              <span key={i} style={{
                                padding: '3px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                background: colors.bgTertiary,
                                color: colors.textPrimary,
                              }}>
                                {kw}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Assigned library */}
                    {campaign.categorization?.assigned_library && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '12px',
                        padding: '10px 14px',
                        background: 'rgba(17, 153, 142, 0.1)',
                        borderRadius: '8px',
                        width: 'fit-content'
                      }}>
                        <i className="bi bi-collection-fill" style={{ color: '#11998e' }}></i>
                        <span style={{ fontSize: '13px', color: '#11998e' }}>
                          ספרייה: <strong>{campaign.categorization.assigned_library.name}</strong>
                        </span>
                      </div>
                    )}

                    {/* Sample posts preview */}
                    {campaign.sample_posts && campaign.sample_posts.length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '8px' }}>פוסטים לדוגמה:</div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {campaign.sample_posts.slice(0, 2).map((post, i) => (
                            <div key={i} style={{
                              background: colors.bgTertiary,
                              borderRadius: '8px',
                              padding: '10px 12px',
                              fontSize: '12px',
                              color: colors.textMuted,
                              maxWidth: '300px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {post.post_content?.substring(0, 80) || post.name || 'פוסט ללא תוכן'}...
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 28px',
                gap: '8px',
              }}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    border: 'none',
                    background: currentPage === 1 ? colors.bgPrimary : colors.bgTertiary,
                    color: currentPage === 1 ? colors.textMuted : colors.textPrimary,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <span style={{ color: colors.textMuted, margin: '0 16px' }}>
                  עמוד {currentPage} מתוך {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    border: 'none',
                    background: currentPage === totalPages ? colors.bgPrimary : colors.bgTertiary,
                    color: currentPage === totalPages ? colors.textMuted : colors.textPrimary,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Suggested Libraries Tab */}
      {activeTab === 'suggested-libraries' && (
        <div style={{
          background: colors.bgSecondary,
          borderRadius: '20px',
          border: `1px solid ${colors.borderColor}`,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}>
          <div style={{
            padding: '20px 24px',
            background: colors.bgTertiary,
            borderBottom: `1px solid ${colors.borderColor}`,
          }}>
            <h3 style={{ color: colors.textPrimary, margin: 0, fontSize: '16px' }}>
              ספריות מוצעות (מדורגות לפי יעילות)
            </h3>
            <p style={{ color: colors.textSecondary, margin: '8px 0 0', fontSize: '13px' }}>
              הספריות ממוינות לפי מספר הקמפיינים שישתמשו בהן - צור קודם את הספריות עם ההשפעה הגדולה ביותר
            </p>
          </div>

          {loading ? (
            <div style={{ padding: '80px', textAlign: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: `3px solid ${colors.borderColor}`,
                borderTopColor: '#f093fb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 20px'
              }}></div>
              <div style={{ color: colors.textSecondary }}>טוען...</div>
            </div>
          ) : suggestedLibraries.length === 0 ? (
            <div style={{ padding: '80px', textAlign: 'center' }}>
              <i className="bi bi-lightbulb" style={{ fontSize: '48px', color: colors.textSecondary, display: 'block', marginBottom: '16px' }}></i>
              <div style={{ color: colors.textPrimary, fontSize: '16px', fontWeight: 600 }}>אין ספריות מוצעות</div>
              <div style={{ color: colors.textSecondary, fontSize: '14px', marginTop: '8px' }}>סווג קמפיינים כדי ליצור הצעות לספריות</div>
            </div>
          ) : (
            suggestedLibraries.map((lib, index) => (
              <div
                key={lib._id}
                style={{
                  padding: '20px 24px',
                  borderBottom: `1px solid ${colors.borderColor}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: index === 0
                      ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                      : colors.bgTertiary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: 700,
                    color: index === 0 ? '#fff' : colors.textPrimary
                  }}>
                    #{index + 1}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: colors.textPrimary, fontSize: '16px', marginBottom: '4px' }}>
                      {lib.name}
                    </div>
                    <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: colors.textMuted }}>
                      {lib.industry && (
                        <span>
                          <i className="bi bi-building me-1"></i>
                          {lib.industry.name_he}
                        </span>
                      )}
                      {lib.location && (
                        <span>
                          <i className="bi bi-geo-alt me-1"></i>
                          {lib.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#f093fb' }}>{lib.campaigns_count}</div>
                    <div style={{ fontSize: '11px', color: colors.textSecondary }}>קמפיינים</div>
                  </div>

                  {lib.status === 'created' ? (
                    <span style={{
                      padding: '10px 20px',
                      borderRadius: '10px',
                      background: 'rgba(17, 153, 142, 0.15)',
                      color: '#11998e',
                      fontSize: '14px',
                      fontWeight: 500
                    }}>
                      <i className="bi bi-check-circle me-2"></i>
                      נוצרה
                    </span>
                  ) : (
                    <button
                      onClick={() => handleCreateLibrary(lib._id)}
                      disabled={creatingLibrary === lib._id}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: creatingLibrary === lib._id ? 'not-allowed' : 'pointer',
                        opacity: creatingLibrary === lib._id ? 0.7 : 1,
                      }}
                    >
                      {creatingLibrary === lib._id ? 'יוצר...' : 'צור ספרייה'}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Finalize Modal with Posts */}
      {showFinalizeModal && selectedCampaign && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div
            style={{
              background: '#fff',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '1200px',
              border: '1px solid rgba(0,0,0,0.1)',
              maxHeight: '90vh',
              display: 'flex',
              overflow: 'hidden'
            }}
          >
            {/* Posts Panel - Left Side */}
            <div style={{
              width: '55%',
              borderLeft: `1px solid ${colors.borderColor}`,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
              background: colors.bgPrimary
            }}>
              <div style={{
                padding: '20px 24px',
                borderBottom: `1px solid ${colors.borderColor}`,
                background: colors.bgTertiary
              }}>
                <h4 style={{ color: colors.textPrimary, margin: 0, fontSize: '15px' }}>
                  <i className="bi bi-file-text me-2"></i>
                  פוסטים ({campaignPosts.length})
                </h4>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                {loadingPosts ? (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      border: `3px solid ${colors.borderColor}`,
                      borderTopColor: '#f093fb',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                      margin: '0 auto 12px'
                    }}></div>
                    <div style={{ color: colors.textSecondary, fontSize: '13px' }}>טוען פוסטים...</div>
                  </div>
                ) : campaignPosts.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>
                    אין פוסטים
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {campaignPosts.map((post, index) => (
                      <div
                        key={post._id}
                        style={{
                          background: colors.bgSecondary,
                          borderRadius: '10px',
                          padding: '14px',
                          border: `1px solid ${colors.borderColor}`
                        }}
                      >
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '8px' }}>
                          פוסט #{index + 1}
                          {post.created_at && ` • ${moment(post.created_at).format('DD/MM/YY')}`}
                        </div>

                        {/* Media Preview */}
                        {post.media_url && (
                          <div style={{ marginBottom: '10px' }}>
                            {post.media_url.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i) ? (
                              <img
                                src={toCdnUrl(post.media_url)}
                                alt="Post media"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '200px',
                                  borderRadius: '8px',
                                  objectFit: 'contain',
                                  background: 'rgba(0,0,0,0.3)'
                                }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none'
                                }}
                              />
                            ) : post.media_url.match(/\.(mp4|webm|mov)(\?|$)/i) ? (
                              <video
                                src={toCdnUrl(post.media_url)}
                                controls
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '200px',
                                  borderRadius: '8px',
                                  background: 'rgba(0,0,0,0.3)'
                                }}
                              />
                            ) : (
                              <a
                                href={post.media_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '6px 10px',
                                  background: 'rgba(102, 126, 234, 0.15)',
                                  borderRadius: '6px',
                                  color: '#667eea',
                                  fontSize: '12px',
                                  textDecoration: 'none'
                                }}
                              >
                                <i className="bi bi-link-45deg"></i>
                                צפה במדיה
                              </a>
                            )}
                          </div>
                        )}

                        <div style={{
                          color: colors.textPrimary,
                          fontSize: '13px',
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxHeight: '120px',
                          overflow: 'hidden'
                        }}>
                          {post.post_content || '(אין תוכן)'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Form Panel - Right Side */}
            <div style={{
              width: '45%',
              padding: '28px',
              overflowY: 'auto',
              maxHeight: '90vh'
            }}>
              <h3 style={{ color: colors.textPrimary, marginTop: 0, marginBottom: '16px', fontSize: '18px' }}>
                סיום סיווג: {selectedCampaign.name}
              </h3>

              {/* AI Analyze Button */}
              <button
                onClick={handleAnalyzeInModal}
                disabled={analyzingInModal}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: analyzingInModal
                    ? 'rgba(102, 126, 234, 0.3)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: analyzingInModal ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '16px'
                }}
              >
                <i className={`bi ${analyzingInModal ? 'bi-hourglass-split' : 'bi-robot'}`}></i>
                {analyzingInModal ? 'מנתח עם AI...' : 'נתח עם AI ומלא טופס'}
              </button>

              {/* AI Suggestion Note - show if previously analyzed */}
              {selectedCampaign.categorization?.ai_analysis?.reasoning && (
                <div style={{
                  background: 'rgba(17, 153, 142, 0.1)',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  borderRight: '3px solid #11998e'
                }}>
                  <div style={{ fontSize: '12px', color: '#11998e', marginBottom: '4px', fontWeight: 500 }}>
                    <i className="bi bi-robot me-1"></i> הצעת AI
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textMuted }}>
                    {selectedCampaign.categorization.ai_analysis.reasoning}
                  </div>
                </div>
              )}

              {/* Industry selection */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ color: colors.textMuted, fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                  תעשייה
                </label>
                <select
                  value={finalizeData.industry_id}
                  onChange={(e) => setFinalizeData({ ...finalizeData, industry_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: `2px solid ${colors.borderColor}`,
                    background: colors.bgTertiary,
                    color: colors.textPrimary,
                    fontSize: '14px',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">בחר תעשייה</option>
                  {industries.map(ind => (
                    <option key={ind._id} value={ind._id}>{ind.name_he}</option>
                  ))}
                </select>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '10px',
                  color: colors.textMuted,
                  fontSize: '12px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={finalizeData.create_new_industry}
                    onChange={(e) => setFinalizeData({ ...finalizeData, create_new_industry: e.target.checked })}
                  />
                  צור תעשייה חדשה
                </label>

                {finalizeData.create_new_industry && (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <input
                      type="text"
                      placeholder="שם באנגלית"
                      value={finalizeData.new_industry_name}
                      onChange={(e) => setFinalizeData({ ...finalizeData, new_industry_name: e.target.value })}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        background: colors.bgTertiary,
                        border: `2px solid ${colors.borderColor}`,
                        borderRadius: '8px',
                        color: colors.textPrimary,
                        fontSize: '13px',
                        outline: 'none',
                      }}
                    />
                    <input
                      type="text"
                      placeholder="שם בעברית"
                      value={finalizeData.new_industry_name_he}
                      onChange={(e) => setFinalizeData({ ...finalizeData, new_industry_name_he: e.target.value })}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        background: colors.bgTertiary,
                        border: `2px solid ${colors.borderColor}`,
                        borderRadius: '8px',
                        color: colors.textPrimary,
                        fontSize: '13px',
                        outline: 'none',
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Location */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ color: colors.textMuted, fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                  מיקום
                </label>
                <input
                  type="text"
                  placeholder="לדוגמה: ירושלים, תל אביב, ארצי"
                  value={finalizeData.location}
                  onChange={(e) => setFinalizeData({ ...finalizeData, location: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: colors.bgTertiary,
                    border: `2px solid ${colors.borderColor}`,
                    borderRadius: '10px',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    outline: 'none',
                  }}
                />
              </div>

              {/* Skip library option */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: colors.textMuted,
                  fontSize: '13px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={finalizeData.skip_library}
                    onChange={(e) => setFinalizeData({
                      ...finalizeData,
                      skip_library: e.target.checked,
                      assign_to_existing_library_id: '',
                      library_name: ''
                    })}
                  />
                  ללא ספרייה (סיווג בלבד)
                </label>
              </div>

              {/* Library assignment - only show if not skipping */}
              {!finalizeData.skip_library && (
                <>
                  <div style={{ marginBottom: '18px' }}>
                    <label style={{ color: colors.textMuted, fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                      הקצה לספרייה קיימת
                    </label>
                    <select
                      value={finalizeData.assign_to_existing_library_id}
                      onChange={(e) => setFinalizeData({ ...finalizeData, assign_to_existing_library_id: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: `2px solid ${colors.borderColor}`,
                        background: colors.bgTertiary,
                        color: colors.textPrimary,
                        fontSize: '14px',
                        outline: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="">בחר ספרייה (או השאר ריק ליצירת חדשה)</option>
                      {publicLibraries.map(lib => (
                        <option key={lib._id} value={lib._id}>{lib.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Suggested library name */}
                  {!finalizeData.assign_to_existing_library_id && (
                    <div style={{ marginBottom: '18px' }}>
                      <label style={{ color: colors.textMuted, fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                        שם לספרייה חדשה
                      </label>
                      <input
                        type="text"
                        placeholder='לדוגמה: נדל"ן ירושלים'
                        value={finalizeData.library_name}
                        onChange={(e) => setFinalizeData({ ...finalizeData, library_name: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: colors.bgTertiary,
                          border: `2px solid ${colors.borderColor}`,
                          borderRadius: '10px',
                          color: colors.textPrimary,
                          fontSize: '14px',
                          outline: 'none',
                        }}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Notes */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ color: colors.textMuted, fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                  הערות (לעיון עתידי)
                </label>
                <textarea
                  placeholder="הוסף הערות או תיאור לקמפיין זה..."
                  value={finalizeData.notes}
                  onChange={(e) => setFinalizeData({ ...finalizeData, notes: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: colors.bgTertiary,
                    border: `2px solid ${colors.borderColor}`,
                    borderRadius: '10px',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  onClick={() => setShowFinalizeModal(false)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '10px',
                    border: `1px solid ${colors.borderColor}`,
                    background: 'transparent',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  ביטול
                </button>
                <button
                  onClick={handleFinalize}
                  disabled={finalizing}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: finalizing ? 'not-allowed' : 'pointer',
                    opacity: finalizing ? 0.7 : 1,
                  }}
                >
                  {finalizing ? 'שומר...' : 'סיים סיווג'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Posts Viewer Modal */}
      {showPostsModal && viewingCampaign && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div
            style={{
              background: '#fff',
              borderRadius: '20px',
              padding: '32px',
              width: '100%',
              maxWidth: '800px',
              border: '1px solid rgba(0,0,0,0.1)',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ color: colors.textPrimary, margin: 0, fontSize: '18px' }}>
                  פוסטים: {viewingCampaign.name}
                </h3>
                {viewingCampaign.account && (
                  <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '4px' }}>
                    {viewingCampaign.account.first_name} {viewingCampaign.account.last_name}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowPostsModal(false)}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  border: 'none',
                  background: colors.bgTertiary,
                  color: colors.textPrimary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
              {loadingPosts ? (
                <div style={{ padding: '60px', textAlign: 'center' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: `3px solid ${colors.borderColor}`,
                    borderTopColor: '#f093fb',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    margin: '0 auto 16px'
                  }}></div>
                  <div style={{ color: colors.textSecondary }}>טוען פוסטים...</div>
                </div>
              ) : campaignPosts.length === 0 ? (
                <div style={{ padding: '60px', textAlign: 'center', color: colors.textSecondary }}>
                  אין פוסטים לקמפיין זה
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {campaignPosts.map((post, index) => (
                    <div
                      key={post._id}
                      style={{
                        background: colors.bgTertiary,
                        borderRadius: '12px',
                        padding: '16px',
                        border: `1px solid ${colors.borderColor}`
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                          פוסט #{index + 1 + (postsPage - 1) * 20}
                        </span>
                        {post.created_at && (
                          <span style={{ fontSize: '11px', color: colors.textSecondary }}>
                            {moment(post.created_at).format('DD/MM/YYYY HH:mm')}
                          </span>
                        )}
                      </div>

                      {post.name && (
                        <div style={{ fontWeight: 600, color: colors.textPrimary, marginBottom: '8px', fontSize: '14px' }}>
                          {post.name}
                        </div>
                      )}

                      <div style={{
                        color: colors.textPrimary,
                        fontSize: '14px',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {post.post_content || '(אין תוכן)'}
                      </div>

                      {post.media_url && (
                        <div style={{ marginTop: '12px' }}>
                          <a
                            href={post.media_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              background: 'rgba(102, 126, 234, 0.15)',
                              borderRadius: '6px',
                              color: '#667eea',
                              fontSize: '12px',
                              textDecoration: 'none'
                            }}
                          >
                            <i className="bi bi-image"></i>
                            צפה במדיה
                          </a>
                        </div>
                      )}

                      {post.reply_text && (
                        <div style={{
                          marginTop: '12px',
                          padding: '10px 12px',
                          background: 'rgba(240, 147, 251, 0.08)',
                          borderRadius: '8px',
                          borderRight: '3px solid #f093fb'
                        }}>
                          <div style={{ fontSize: '11px', color: '#f093fb', marginBottom: '4px' }}>תגובה אוטומטית:</div>
                          <div style={{ fontSize: '13px', color: colors.textPrimary }}>{post.reply_text}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {postsTotalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                paddingTop: '16px',
                borderTop: `1px solid ${colors.borderColor}`
              }}>
                <button
                  onClick={() => openPostsModal(viewingCampaign, postsPage - 1)}
                  disabled={postsPage === 1}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    border: 'none',
                    background: postsPage === 1 ? colors.bgPrimary : colors.bgTertiary,
                    color: postsPage === 1 ? colors.textMuted : colors.textPrimary,
                    cursor: postsPage === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <span style={{ color: colors.textMuted, margin: '0 12px', fontSize: '13px' }}>
                  עמוד {postsPage} מתוך {postsTotalPages}
                </span>
                <button
                  onClick={() => openPostsModal(viewingCampaign, postsPage + 1)}
                  disabled={postsPage === postsTotalPages}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    border: 'none',
                    background: postsPage === postsTotalPages ? colors.bgPrimary : colors.bgTertiary,
                    color: postsPage === postsTotalPages ? colors.textMuted : colors.textPrimary,
                    cursor: postsPage === postsTotalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        select option {
          background: #fff;
          color: #1e1e2d;
        }
      `}</style>
    </div>
  )
}

export default CampaignCategorizationPage
